export const HV_JWT_KEY = 'hv_jwt';
export const HV_JWT_EXPIRED_EVENT = 'hv-jwt-expired';

export function getHvJwtRaw() {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(HV_JWT_KEY);
  } catch {
    return null;
  }
}

/** Returns hv_jwt only if it exists, is a JWT, and exp is in the future. */
export function getValidHvJwt() {
  const token = getHvJwtRaw();
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad) base64 += '='.repeat(4 - pad);
    const payload = JSON.parse(atob(base64));
    if (payload?.exp && payload.exp * 1000 <= Date.now()) return null;
    return token;
  } catch {
    return null;
  }
}

export function setAuthorizationHeader(headers, token) {
  const value = `Bearer ${token}`;
  if (headers && typeof headers.set === 'function') {
    headers.set('Authorization', value);
    return headers;
  }
  const next = headers || {};
  next.Authorization = value;
  return next;
}

/** Pathname from an Axios url + optional baseURL (relative or absolute). */
export function axiosRequestPath(url, baseURL) {
  const raw = String(url || '');
  if (!raw) return '';
  try {
    if (/^https?:\/\//i.test(raw)) {
      return new URL(raw).pathname;
    }
    if (baseURL && /^https?:\/\//i.test(String(baseURL))) {
      return new URL(raw, baseURL).pathname;
    }
    const path = raw.split('?')[0];
    return path.startsWith('/') ? path : `/${path}`;
  } catch {
    return String(raw).split('?')[0];
  }
}

/**
 * APIs that use getUserInfo / authenticateJWT. Without a valid hv_jwt these
 * only produce 401 No token — do not send them.
 *
 * Public apply-page association routes are excluded: guests must be able to
 * create an association + save assessment without logging in.
 */
export function isHvJwtRequiredPath(url, baseURL) {
  const path = axiosRequestPath(url, baseURL);
  if (path === '/api/me' || path.startsWith('/api/me/')) return true;
  if (path === '/api/ee-documents' || path.startsWith('/api/ee-documents/')) return true;
  if (path === '/api/associations' || path.startsWith('/api/associations/')) {
    const publicApplyAssociationPaths = [
      '/api/associations/add-candidates-to-role',
      '/api/associations/add-candidates-to-role1',
      '/api/associations/save-assessment',
      '/api/associations/check-association',
    ];
    if (publicApplyAssociationPaths.some((p) => path === p)) return false;
    return true;
  }
  if (path === '/api/admin' || path.startsWith('/api/admin/')) return true;
  return false;
}

/**
 * If localStorage has a dead JWT, drop it (same as AuthProvider on page load).
 * Returns true when a token was removed.
 */
export function discardInvalidHvJwt() {
  const raw = getHvJwtRaw();
  if (!raw) return false;
  if (getValidHvJwt()) return false;
  try {
    localStorage.removeItem(HV_JWT_KEY);
  } catch {
    // ignore quota / private-mode
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(HV_JWT_EXPIRED_EVENT));
  }
  return true;
}
